<template>
    <div :class="isPanel ? 'buyflow-panel' : ''">
        <div class="text-xl desktop:text-2xl
                    font-bold
                    pb-4"
            :class="centerHeading ? 'text-center' : ''">
            Find a Channel
        </div>
        <div class="relative">
            <input
                v-model.trim="search"
                placeholder="Enter the name of a channel"
                type="text"
                class="block h-10 w-full rounded-full" >
            <IconSearch class="absolute right-0 top-0 my-3 mx-3" />
        </div>
        <!--  Search Results   -->
        <div v-if="visibleChannels.length"
             class="mt-4 -mb-6 -mr-6 pr-3 max-h-100 overflow-y-scroll">
            <div class="divide-y">
                <div v-for="(channel, key) in visibleChannels"
                     :key="key"
                     class="py-4">
                    <div class="grid
                            grid-cols-6
                            gap-x-2
                            justify-end
                            items-center
                            desktop:mr-4">
                        <div class="col-span-3">
                            <div class="text-lg
                                    font-bold">
                                {{ channel.channelName.replace('*', '') }}
                            </div>
                        </div>
                        <div v-if="channel.category === 'Locals+'"
                             class="justify-self-end
                                col-span-3">
                            Included in base channels
                        </div>
                        <div v-else-if="packageOfChannel(channel) == null"
                             class="justify-self-end
                                    col-span-3">
                            Not currently available in your area
                        </div>
                        <div v-else
                             v-for="channelPackage in [packageOfChannel(channel)]"
                             class="grid
                                    col-span-6 desktop:col-span-3
                                    grid-cols-3
                                    justify-end
                                    items-center
                                    py-0 desktop:py-1">
                            <div class="col-span-2 desktop:col-span-1 text-salmon desktop:text-navy">
                                <div class="text-lg
                                            font-bold">
                                    {{ channelPackage.name }}
                                    <AppModal>
                                        <template #trigger>
                                            <IconCircleInfo class="text-sm cursor-pointer" />
                                        </template>
                                        <template #contents>
                                            <div class="text-sm
                                                        font-normal
                                                        break-words">
                                                This channel is included with the <strong>{{ channelPackage.name }}</strong> channel package
                                                <br><br>
                                                {{ channelPackage.subtitle }}
                                            </div>
                                        </template>
                                    </AppModal>
                                </div>
                            </div>
                            <div class="col-span-1
                                        justify-self-center
                                        hidden desktop:block">
                                <span class="text-salmon
                                             font-bold
                                             -mx-0.5">
                                    {{ toCurrency(channelPackage.price) }}
                                </span>
                                <span class="text-sm">/mo{{ isFamilyPlus(channelPackage) ? '*': ''}}</span>
                            </div>
                            <div class="justify-self-end
                                        col-span-1
                                        text-center
                                        -mt-8 desktop:-mt-0">
                                <div class="block desktop:hidden
                                            mb-1">
                                    <span class="text-salmon
                                                 font-bold
                                                 text-sm
                                                 -mx-0.5">
                                        {{ toCurrency(channelPackage.price) }}
                                    </span>
                                    <span class="text-xs">/mo{{ isFamilyPlus(channelPackage) ? '*': ''}}</span>
                                </div>
                                <button
                                    @click="isPackageSelected(channelPackage)? deselectPackage(channelPackage): selectPackage(channelPackage)"
                                    :class="{ 'active': isPackageSelected(channelPackage) }"
                                    class="buyflow-button flex items-center"
                                >
                                    <template v-if="isPackageSelected(channelPackage)">
                                        <IconCheck class="inline mr-1" />
                                        <span>Added</span>
                                    </template>
                                    <template v-else>
                                        <IconPlus class="inline mr-1" />
                                        <span>Add</span>
                                    </template>
                                </button>
                            </div>
                        </div>
                        <div v-if="packageOfChannel(channel) && packageOfChannel(channel).name === 'Family+'"
                             class="col-span-6">
                            *Adding Family+ will also add Variety+ for <span class="text-salmon">{{toCurrency(channelData.packages.varietyPlus.price)}}/mo</span> if not already in your cart
                        </div>
                    </div>
                </div>
            </div>
            <button
                v-if="!showAllResults && results.length > 5"
                @click.prevent="showAllResults = true"
                class="buyflow-panel-expander block my-6 py-2 text-xs desktop:text-sm flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-full text-center text-navy-dark w-full whitespace-nowrap"
            >
                Show all results <IconCaretDown class="inline ml-1" />
            </button>
        </div>
        <div 
            v-else-if="visibleChannels.length === 0 && search !== ''"
            class="mt-6">
            No search results with keyword <span class="font-bold">{{ search }}</span>
        </div>
    </div>
    
</template>

<script>
    import AppModal from "~/Buyflow/components/AppModal"
    import IconCaretDown from "@/atoms/icons/IconCaretDown"
    import IconCheck from "@/atoms/icons/IconCheck"
    import IconCircleInfo from "@/atoms/icons/IconCircleInfo"
    import IconPlus from "@/atoms/icons/IconPlus"
    import IconSearch from "@/atoms/icons/IconSearch"

    export default {
        name: 'TVChannelSearch',
        components: {
            AppModal,
            IconCaretDown,
            IconCheck,
            IconCircleInfo,
            IconPlus,
            IconSearch,
        },
        data:() => ({
            search: '',
            showAllResults: false,
        }),
        props: {
            channelData: {
                type: Object,
                required: true,
            },
            mainService: {
                type: Object,
                required: false,
            },
            localsPlus: {
                type: Object,
                required: true,
            },
            filteredChannels: {
                type: Object,
                default: () => ({
                    localsPlus: [],
                    varietyPlus: [],
                    familyPlus: [],
                    addOns: [],
                }),
            },
            isPackageSelected: {
                type: Function,
                required: true,
            },
            hasSelectedAService: {
                type: Boolean,
                default: false,
            },
            centerHeading: {
                type: Boolean,
                require: false
            },
            isPanel: {
                type: Boolean,
                default: true,
            }
        },
        computed: {
            addOns() {
                return [
                    ...this.channelData.premiumChannels,
                    ...this.channelData.internationalChannels,
                ]
            },
            visibleChannels() {
                if (this.results.length === 0){
                    this.showAllResults = false
                }
                return this.showAllResults ? this.results : this.results.slice(0, 5)
            },
            results(){
                const regexMatch = new RegExp(this.search, 'gi')
                return this.search? [
                    ...this.filteredChannels.localsPlus,
                    ...this.filteredChannels.varietyPlus,
                    ...this.filteredChannels.familyPlus,
                    ...this.filteredChannels.addOns,
                ].filter(item => item.channelName.match(regexMatch)) || []: []
            },
        },
        methods:{
            isFamilyPlus(channelPackage){
                return channelPackage.name === 'Family+'
            },
            packageOfChannel(channel){
                if (channel.subCategory){
                    return {
                        'HBO': this.addOns.find(item => item.name === 'Max (includes HBO ch.)'),
                        'STARZ ENCORE': this.addOns.find(item => item.name === 'StarzEncore'),
                    }[channel.subCategory] || this.addOns.find(item => item.name.toLowerCase() === channel.subCategory.toLowerCase()) || null
                } else {
                    return {
                        'Variety+': this.channelData.packages.varietyPlus,
                        'Family+': this.channelData.packages.familyPlus,
                    }[channel.category]|| null
                }
            },
            selectPackage(channelPackage){
                if(!this.hasSelectedAService) {
                    this.$emit('select-service', {
                        service: {
                            decision: this.localsPlus.decision,
                            selection: this.localsPlus
                        },
                        equipment: {
                            decision: this.mainService.decision,
                            selection: this.mainService
                        },
                        declined: false
                    })
                }
                if (this.isFamilyPlus(channelPackage) && !this.isPackageSelected(this.channelData.packages.varietyPlus)){
                    this.$emit('select-package', [this.channelData.packages.varietyPlus, channelPackage])
                } else {
                    this.$emit('select-package', channelPackage)
                }
            },
            deselectPackage(channelPackage) {
                if (channelPackage.name === 'Variety+' && this.isPackageSelected(this.channelData.packages.familyPlus)){
                    this.$emit('select-package', [channelPackage, this.channelData.packages.familyPlus])
                } else {
                    this.$emit('select-package', channelPackage)
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    table {
        @apply border
               border-gray-200
               border-collapse
               w-full;

        thead {
            @apply bg-gray-100;
        }

        th, td {
            @apply border
                   border-gray-200
                   desktop:whitespace-nowrap
                   p-2;
        }

        th {
            @apply text-sm desktop:text-base
                   font-bold;
        }

        td {
            @apply text-sm;
        }
    }

    table.striped-rows {
        tr:nth-child(even) {
            @apply bg-gray-50;
        }
    }

    table.striped-cats {
        .cat {
            @apply bg-gray-50;
        }
    }

    table.numbered {
        td:first-child {
            @apply text-center
                   font-bold;
        }
    }
</style>
